/**
 * Bridge between the two tool-id spaces the recommendation path spans.
 *
 *   - shape-matrix / methodRegistry `toolId`  ('dot-voting', 'simple-weighted-scoring')
 *     — the space the situation rules are authored in.
 *   - registry compatibility id                ('dot_voting', 'simple_weighted_scoring')
 *     — the space session surfaces select in (REGISTRY_SESSION_TOOL_IDS,
 *       SESSION_TOOL_DEFINITIONS, LiveToolRunner).
 *
 * Built on `normalizeSessionToolId` (sessionToolCatalog) rather than a parallel
 * table, so an alias added there is honoured here.
 */

import { REGISTRY_TOOL_SPECS_BY_ID } from '../generated/registryToolSpecs';
import { METHOD_REGISTRY } from './methodRegistry';
import { normalizeSessionToolId } from './sessionToolCatalog';

/** 'dot-voting' → 'dot_voting'. Undefined when the id does not resolve to a registry tool. */
export function matrixToolIdToRegistryId(matrixToolId: string): string | undefined {
  const normalized = normalizeSessionToolId(matrixToolId);
  return normalized && REGISTRY_TOOL_SPECS_BY_ID[normalized] ? normalized : undefined;
}

const REGISTRY_TO_MATRIX: Readonly<Record<string, string>> = (() => {
  const out: Record<string, string> = {};
  for (const method of METHOD_REGISTRY) {
    for (const variant of method.variants) {
      const registryId = matrixToolIdToRegistryId(variant.toolId);
      if (registryId && !out[registryId]) out[registryId] = variant.toolId;
    }
  }
  return out;
})();

/** 'dot_voting' → 'dot-voting'. Undefined when no method-registry variant maps to the id. */
export function registryIdToMatrixToolId(registryId: string): string | undefined {
  return REGISTRY_TO_MATRIX[registryId];
}

/** Map a list, dropping ids that do not resolve. Order preserved, duplicates removed. */
export function matrixToolIdsToRegistryIds(ids: readonly string[]): string[] {
  const out: string[] = [];
  for (const id of ids) {
    const mapped = matrixToolIdToRegistryId(id);
    if (mapped && !out.includes(mapped)) out.push(mapped);
  }
  return out;
}

export function registryIdsToMatrixToolIds(ids: readonly string[]): string[] {
  const out: string[] = [];
  for (const id of ids) {
    const mapped = registryIdToMatrixToolId(id);
    if (mapped && !out.includes(mapped)) out.push(mapped);
  }
  return out;
}
